import axios from "axios";
import {
  BACKEND_URL,
  HOSTELS_API_URL,
  HR_API_URL,
  OTP_SEND_PATH,
  OTP_VERIFY_PATH,
  STAFF_PROFILE_PATH,
  isBackendConfigured,
  isHostelsApiConfigured,
  resolveApiUrl,
} from "./config";
import {
  formatApiError,
  getAccessToken,
  isAccessTokenExpired,
  normalizePhone,
  parseAuthResponse,
  setAccessToken,
  setStoredPermissions,
  setStoredSessionUser,
} from "./auth";
import { attachSessionExpiryInterceptor } from "./sessionExpiry";
import {
  isStaffUserType,
  isStudentFromHrProfile,
  isStudentSessionUser,
} from "./userRole";

const authClient = axios.create({
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

authClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token && !isAccessTokenExpired(token)) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
attachSessionExpiryInterceptor(authClient);

function isQidLikeDisplay(value, qid) {
  const text = String(value ?? "").trim();
  if (!text) return true;
  if (qid && text.toUpperCase() === String(qid).trim().toUpperCase()) return true;
  return /^QT[A-Z0-9]+$/i.test(text);
}

function resolveDisplayName(profile, qid) {
  const name = String(profile.name ?? "").trim();
  if (name && !isQidLikeDisplay(name, qid)) return name;
  return null;
}

function mapProfile(raw, phone) {
  const profile = raw?.data ?? raw ?? {};
  const info = profile.info ?? {};
  const qid = profile.id || profile.qid || "user";
  const userType = String(profile.type ?? "").trim().toLowerCase();
  const isStudent = isStudentFromHrProfile(profile, info);

  const displayName = resolveDisplayName(profile, qid);

  return {
    id: qid,
    qid,
    name: displayName || (isStudent ? "Student" : "Staff"),
    role: isStudent ? "requester" : "team_member",
    user_type: isStaffUserType(userType) ? "staff" : isStudent ? "student" : userType || "staff",
    mobile: profile.number ?? profile.phone_number ?? phone,
    email: profile.mail || profile.mail_id,
    staff_id: profile.eid || null,
    student_id: profile.htno || null,
    title: isStudent
      ? info.program || info.department || null
      : info.designation || info.department || info.program || null,
    department_name: info.department || null,
    department_id: profile.department_id ?? null,
  };
}

function mapHostelsStudentProfile(row) {
  if (!row) return null;

  const qid = row.qid;
  const htno = row.student_details?.htno || null;
  const displayName = resolveDisplayName({ name: row.name }, qid);

  return {
    id: qid,
    qid,
    name: displayName || "Student",
    role: "requester",
    user_type: "student",
    student_id: htno,
    email: row.mail_id || null,
    mobile: row.phone_number || null,
    hostel_block: row.hostel_details?.block_id || null,
    room_number: row.hostel_details?.room_no || null,
  };
}

async function fetchHostelsStudentProfile() {
  if (!isHostelsApiConfigured() || !getAccessToken()) return null;

  try {
    const base = HOSTELS_API_URL.replace(/\/+$/, "");
    const { data } = await authClient.get(`${base}/api/userInfoForLeave`);
    const row = Array.isArray(data) ? data[0] : data?.data?.[0] ?? data?.data ?? data;
    return mapHostelsStudentProfile(row);
  } catch {
    return null;
  }
}

async function mergeStudentHostelProfile(user) {
  if (isStaffUserType(user?.user_type)) return user;

  const hostelProfile = await fetchHostelsStudentProfile();
  if (!hostelProfile) return user;

  const qid = user?.qid || user?.id;
  const name =
    resolveDisplayName({ name: user?.name }, qid) ||
    hostelProfile.name ||
    user?.name ||
    "Student";

  return {
    ...user,
    ...hostelProfile,
    name,
    student_id: hostelProfile.student_id || user?.student_id || null,
    role: "requester",
    user_type: "student",
    mobile: user?.mobile || hostelProfile.mobile,
    email: user?.email || hostelProfile.email,
  };
}

/** POST /api/v1/otp/send */
export async function sendOtp(phone) {
  const phone_number = normalizePhone(phone);
  if (phone_number.length < 10) {
    throw new Error("Enter a valid 10-digit mobile number");
  }
  if (!isBackendConfigured()) {
    throw new Error("REACT_APP_BACKEND_URL is not configured");
  }

  try {
    const { data } = await authClient.post(resolveApiUrl(OTP_SEND_PATH), { phone_number });
    return {
      phone_number: data?.data?.phone_number ?? phone_number,
      message: data?.message || "OTP sent successfully",
    };
  } catch (err) {
    throw new Error(formatApiError(err));
  }
}

/** POST /api/v1/otp/verify — returns parsed session payload. */
export async function verifyOtp(phone, otp) {
  const phone_number = normalizePhone(phone);
  const code = String(otp || "").trim();

  if (!isBackendConfigured()) {
    throw new Error("REACT_APP_BACKEND_URL is not configured");
  }

  try {
    const { data } = await authClient.post(resolveApiUrl(OTP_VERIFY_PATH), {
      phone_number,
      otp: code,
    });
    const parsed = parseAuthResponse(data);
    if (parsed.success === false) {
      throw new Error(parsed.message || "OTP verification failed");
    }
    if (!parsed.token) {
      throw new Error("Missing access token in login response");
    }
    return {
      ...parsed,
      phone_number,
    };
  } catch (err) {
    throw new Error(formatApiError(err));
  }
}

/** Fetch staff/student profile after OTP verify (optional enrichment). */
export async function fetchProfile(phone) {
  let user = null;

  try {
    const { data } = await authClient.get(resolveApiUrl(STAFF_PROFILE_PATH, HR_API_URL));
    user = mapProfile(data, phone);
  } catch {
    /* HR profile optional — fall back below */
  }

  if (!user) {
    user = await enrichUserTypeFromHostels();
  }

  if (!user) return null;

  if (isStudentSessionUser(user)) {
    user = await mergeStudentHostelProfile(user);
  }

  return user;
}

/** When HR profile is unavailable, infer student vs staff from hostels auth. */
async function enrichUserTypeFromHostels() {
  if (!isHostelsApiConfigured() || !getAccessToken()) return null;

  try {
    const base = HOSTELS_API_URL.replace(/\/+$/, "");
    const { data } = await authClient.get(`${base}/api/v1/auth/me`);
    const user = data?.data?.user ?? data?.user;
    const userType = String(user?.type ?? "").trim().toLowerCase();

    if (userType === "student") {
      return mergeStudentHostelProfile({
        id: user.qid,
        qid: user.qid,
        role: "requester",
        user_type: "student",
      });
    }

    if (userType === "staff") {
      return {
        id: user.qid,
        qid: user.qid,
        name: "Staff",
        role: "team_member",
        user_type: "staff",
      };
    }
  } catch {
    /* ignore */
  }

  return null;
}

/** Persist JWT and return the user object for the app shell. */
export async function establishSession(session, phone) {
  const phone_number = normalizePhone(phone);

  setAccessToken(session.token);
  const rbac = Array.isArray(session.permissions) ? session.permissions : [];
  setStoredPermissions(rbac);

  let user =
    session.user ||
    (session.qid
      ? {
          id: session.qid,
          qid: session.qid,
          name: "User",
          role: "team_member",
          mobile: phone_number,
        }
      : null);

  const profile = await fetchProfile(phone_number);
  if (profile) {
    user = { ...user, ...profile, id: profile.id || user?.id, mobile: phone_number };
  }

  user = { ...user, rbac };

  if (user?.id) {
    setStoredSessionUser(user);
  }

  return user;
}
